import { PACKAGE_STORAGE, SELECTED_FLIGHTS_STORAGE } from '@config/storageKeys';
import { useCurrencyContext } from '@contexts/currency-context';
import { usePackagesParams } from '@hooks/use-packages-params';
import useStorage from '@hooks/useStorage';
import type { PropertyDetails } from '@interfaces/expedia/property';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import type { CheckoutHeaderQuote } from '@v2/components/BuyPackageHeader/checkout-header-quote';
import { getEffectivePassengerCount } from '@v2/views/checkout/components/CheckoutForm/utils';
import { getFlightFees } from '@v2/views/checkout/containers/CheckoutAsideCards/components/util';
import { asRecord, readHotelStorage } from '../room-prefill';

type HotelEstimateSource =
  | { kind: 'list'; hotelPrice: number; ready: boolean }
  | {
      kind: 'details';
      property: Pick<PropertyDetails, 'id' | 'minimumTotalPrice'> | null;
      ready: boolean;
    };

export function useHotelEstimate(source: HotelEstimateSource) {
  const storage = useStorage();
  const { packageType } = usePackagesParams();
  const { currency, priceRate } = useCurrencyContext();
  const hydrated = useBuildPackagesStore((state) => state._hasHydrated);
  const roomConfigurations = useBuildPackagesStore(
    (state) => state.roomConfigurations
  );
  const selectedTicketPrice = useBuildPackagesStore(
    (state) => state.selectedTicketPrice
  );
  const tickets = useBuildPackagesStore((state) => state.tickets);
  const roomSelections = useSearchHotelStore((state) => state.roomSelections);
  const selectedHotelId = useSearchHotelStore((state) => state.selectedHotelId);
  const packageData = readHotelStorage(storage, PACKAGE_STORAGE);
  const selected = asRecord(packageData.selected);
  const flightData = readHotelStorage(storage, SELECTED_FLIGHTS_STORAGE);
  const passengers = asRecord(flightData.passengers);
  const travelers = getEffectivePassengerCount(
    {
      selected: { ...selected, hotel: true },
      tickets:
        typeof packageData.tickets === 'number' ? packageData.tickets : tickets,
      roomConfigurations,
    },
    typeof passengers.adults === 'number'
      ? {
          passengers: {
            adults: passengers.adults,
            children:
              typeof passengers.children === 'number' ? passengers.children : 0,
            infants:
              typeof passengers.infants === 'number' ? passengers.infants : 0,
          },
        }
      : null
  );
  const fare = asRecord(flightData.simpleFare);
  const flightPrice = Number(fare.packagePrice ?? 0);
  const flightTotal = selected.flights
    ? flightPrice + getFlightFees(flightPrice)
    : 0;
  const currentRooms =
    source.kind === 'details' &&
    selectedHotelId !== null &&
    selectedHotelId !== source.property?.id
      ? []
      : roomSelections;
  const isRoomFilled = roomConfigurations.length === currentRooms.length;
  const roomsTotal = currentRooms.reduce((sum, room) => sum + room.price, 0);
  const selectedHotelAndFlightTotal = roomsTotal + flightTotal;
  const hotelPrice =
    source.kind === 'list'
      ? source.hotelPrice
      : isRoomFilled
        ? roomsTotal
        : (source.property?.minimumTotalPrice ?? 0);
  const includesTickets =
    selected.tickets || (source.kind === 'list' && packageType === 'packages');
  const amount =
    hotelPrice + flightTotal + (includesTickets ? selectedTicketPrice : 0);
  const ready =
    hydrated &&
    source.ready &&
    travelers > 0 &&
    (source.kind === 'list' || source.property !== null);
  const quote: CheckoutHeaderQuote = {
    amount: ready ? amount : null,
    travelers,
    currency,
    ...(ready
      ? {
          display: {
            perPerson: formatCurrencyV2(
              amount / travelers,
              priceRate,
              currency,
              true,
              false
            ),
            total: formatCurrencyV2(amount, priceRate, currency, true, false),
          },
        }
      : {}),
  };

  return { quote, selectedHotelAndFlightTotal, isRoomFilled };
}
